import { PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { PatientPersonalProfile, VitalsEntity } from '../../../../../store/types/prescription';
import {
    GROWTH_CHART_NAME_KEY_GROUP_MAPPING,
    GROWTH_CHART_RESULTS,
} from '../../../../../constants';
import GrowthChartRightPopUp from './GrowthChartRightPopUp';
import { AnimatePresence } from 'framer-motion';
import { getPatientPersonalProfile } from '../../../../../store/selectors/prescriptionPad';
import { GlobalState } from '../../../../../store/reducers';
import { getAgeInYearsWithoutRoundOff } from '../../../../../store/utils';
import { track } from '../../../../../tracking';

type Props = {
    elements: ReadonlyArray<VitalsEntity>;
    containerId?: string;
    onElementUpdate: (option: VitalsEntity, index?: number) => void;
    onElementDelete: (id: string, index?: number) => void;
    patientPersonalProfile?: PatientPersonalProfile;
};

type State = {
    openGrowthChartRightPopUp: boolean;
};

export class GrowthChartPad extends PureComponent<Props, State> {
    state: State = {
        openGrowthChartRightPopUp: false,
    };

    parseHeightInFeet = (height: string): number => {
        const heightInFeet = height.split("'");
        return (
            parseFloat(String(heightInFeet[0])) + (parseFloat(String(heightInFeet[1])) / 12 || 0)
        );
    };

    updateElement = (option: VitalsEntity, id: string): void => {
        const { elements, onElementUpdate, onElementDelete } = this.props;
        const index = elements.findIndex((el) => el.id === id);
        if (option.value.qt) {
            onElementUpdate(option, index === -1 ? undefined : index);
        } else {
            onElementDelete(option.id);
        }
    };

    openGowthChartRightPopUp = () => {
        track('GROWTH_CHART_OPEN');
        this.setState({
            openGrowthChartRightPopUp: true,
        });
    };

    closeGowthChartRightPopUp = () => {
        this.setState({
            openGrowthChartRightPopUp: false,
        });
    };

    render(): ReactNode {
        const { openGrowthChartRightPopUp } = this.state;
        const { elements, patientPersonalProfile } = this.props;

        const growthChartVitals = elements?.filter((e) => GROWTH_CHART_RESULTS?.[e?.id]);
        const elementsObject: { [key: string]: VitalsEntity } = growthChartVitals?.reduce(
            (pre, curr) => {
                return {
                    ...pre,
                    [curr.id]: curr,
                };
            },
            {},
        );
        const age = patientPersonalProfile?.age;
        const ageInNumber = getAgeInYearsWithoutRoundOff(age);
        const isPreTerm = elements.find((e) => e.id === 'lb-5257276261');

        return (
            <>
                <div className="flex items-center justify-between gap-x-16 gap-y-12 border-t border-darwin-neutral-100 mt-6 pt-8 text-14 font-400">
                    <div
                        className={`flex ${
                            ageInNumber &&
                            ageInNumber >= 0 &&
                            ageInNumber <= 18 &&
                            (patientPersonalProfile?.gender?.toLowerCase() === 'male' ||
                                patientPersonalProfile?.gender?.toLowerCase() === 'female') &&
                            growthChartVitals?.length
                                ? 'flex-wrap'
                                : ''
                        } items-center gap-x-16 gap-y-12 text-14 font-400`}
                    >
                        <div className="flex flex-wrap items-center">
                            <div className="flex items-center text-12 font-700 uppercase space-x-6 mr-6">
                                <p className="px-10 py-3 text-white bg-darwin-accent-medical-history-orange-800 rounded-4">
                                    new
                                </p>
                                <p className="whitespace-nowrap">
                                    Growth Chart [{isPreTerm ? 'FENTON' : 'WHO/IAP'}]:
                                </p>
                            </div>
                        </div>
                        {ageInNumber ? (
                            !(ageInNumber >= 0 && ageInNumber <= 18) ? (
                                <div className="flex items-center space-x-10 py-6 px-8 w-full bg-darwin-accent-light-yellow rounded-8">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 14 14"
                                        fill="none"
                                    >
                                        <path
                                            d="M7 13.625C4.66406 13.625 2.53125 12.4062 1.36328 10.375C0.195312 8.36914 0.195312 5.90625 1.36328 3.875C2.53125 1.86914 4.66406 0.625 7 0.625C9.31055 0.625 11.4434 1.86914 12.6113 3.875C13.7793 5.90625 13.7793 8.36914 12.6113 10.375C11.4434 12.4062 9.31055 13.625 7 13.625ZM7 3.875C6.64453 3.875 6.39062 4.1543 6.39062 4.48438V7.32812C6.39062 7.68359 6.64453 7.9375 7 7.9375C7.33008 7.9375 7.60938 7.68359 7.60938 7.32812V4.48438C7.60938 4.1543 7.33008 3.875 7 3.875ZM6.1875 9.5625C6.1875 10.0195 6.54297 10.375 7 10.375C7.43164 10.375 7.8125 10.0195 7.8125 9.5625C7.8125 9.13086 7.43164 8.75 7 8.75C6.54297 8.75 6.1875 9.13086 6.1875 9.5625Z"
                                            fill="#8E6807"
                                        />
                                    </svg>
                                    <span className="text-darwin-accent-dark-yellow text-12 font-700">
                                        Age must be between 0 to 18 to use growth chart
                                    </span>
                                </div>
                            ) : patientPersonalProfile?.gender?.toLowerCase() === 'male' ||
                              patientPersonalProfile?.gender?.toLowerCase() === 'female' ? (
                                !growthChartVitals?.length ? (
                                    <div className="flex items-center space-x-10 py-6 px-8 w-full bg-darwin-accent-light-yellow rounded-8">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="14"
                                            height="14"
                                            viewBox="0 0 14 14"
                                            fill="none"
                                        >
                                            <path
                                                d="M7 13.625C4.66406 13.625 2.53125 12.4062 1.36328 10.375C0.195312 8.36914 0.195312 5.90625 1.36328 3.875C2.53125 1.86914 4.66406 0.625 7 0.625C9.31055 0.625 11.4434 1.86914 12.6113 3.875C13.7793 5.90625 13.7793 8.36914 12.6113 10.375C11.4434 12.4062 9.31055 13.625 7 13.625ZM7 3.875C6.64453 3.875 6.39062 4.1543 6.39062 4.48438V7.32812C6.39062 7.68359 6.64453 7.9375 7 7.9375C7.33008 7.9375 7.60938 7.68359 7.60938 7.32812V4.48438C7.60938 4.1543 7.33008 3.875 7 3.875ZM6.1875 9.5625C6.1875 10.0195 6.54297 10.375 7 10.375C7.43164 10.375 7.8125 10.0195 7.8125 9.5625C7.8125 9.13086 7.43164 8.75 7 8.75C6.54297 8.75 6.1875 9.13086 6.1875 9.5625Z"
                                                fill="#8E6807"
                                            />
                                        </svg>
                                        <span className="text-darwin-accent-dark-yellow text-12 font-700">
                                            Add height, weight, BMI or OFC for latest growth chart.
                                        </span>
                                    </div>
                                ) : (
                                    <>
                                        {GROWTH_CHART_NAME_KEY_GROUP_MAPPING.map((gc) => {
                                            const zIndex = elementsObject?.[gc.zIndexId];
                                            const percentile = elementsObject?.[gc.percentile];

                                            if (!zIndex && !percentile) {
                                                return null;
                                            }

                                            return (
                                                <div className="flex items-center space-x-8 p-8 bg-darwin-neutral-50 rounded-4">
                                                    <p className="font-700 text-darwin-neutral-900">
                                                        {gc.name}:
                                                    </p>
                                                    <div className="flex items-center bg-white text-darwin-neutral-900 rounded-4 px-8 py-4 space-x-10">
                                                        {percentile?.value?.qt && (
                                                            <span className="text-darwin-accent-dark-yellow text-13 font-700">
                                                                {percentile?.value?.qt} %ile
                                                            </span>
                                                        )}
                                                        {zIndex?.value?.qt && (
                                                            <span>
                                                                <span className="text-13 font-400">
                                                                    Z-score:{' '}
                                                                </span>
                                                                <span className="text-darwin-green-primary font-700">
                                                                    {zIndex?.value?.qt}
                                                                </span>
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                )
                            ) : (
                                <div className="flex items-center space-x-10 py-6 px-8 w-full bg-darwin-accent-light-yellow rounded-8">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 14 14"
                                        fill="none"
                                    >
                                        <path
                                            d="M7 13.625C4.66406 13.625 2.53125 12.4062 1.36328 10.375C0.195312 8.36914 0.195312 5.90625 1.36328 3.875C2.53125 1.86914 4.66406 0.625 7 0.625C9.31055 0.625 11.4434 1.86914 12.6113 3.875C13.7793 5.90625 13.7793 8.36914 12.6113 10.375C11.4434 12.4062 9.31055 13.625 7 13.625ZM7 3.875C6.64453 3.875 6.39062 4.1543 6.39062 4.48438V7.32812C6.39062 7.68359 6.64453 7.9375 7 7.9375C7.33008 7.9375 7.60938 7.68359 7.60938 7.32812V4.48438C7.60938 4.1543 7.33008 3.875 7 3.875ZM6.1875 9.5625C6.1875 10.0195 6.54297 10.375 7 10.375C7.43164 10.375 7.8125 10.0195 7.8125 9.5625C7.8125 9.13086 7.43164 8.75 7 8.75C6.54297 8.75 6.1875 9.13086 6.1875 9.5625Z"
                                            fill="#8E6807"
                                        />
                                    </svg>
                                    <span className="text-darwin-accent-dark-yellow text-12 font-700">
                                        The growth chart is accessible for individuals of the Male
                                        or Female gender only
                                    </span>
                                </div>
                            )
                        ) : (
                            <div className="flex items-center space-x-10 py-6 px-8 w-full bg-darwin-accent-light-yellow rounded-8">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                >
                                    <path
                                        d="M7 13.625C4.66406 13.625 2.53125 12.4062 1.36328 10.375C0.195312 8.36914 0.195312 5.90625 1.36328 3.875C2.53125 1.86914 4.66406 0.625 7 0.625C9.31055 0.625 11.4434 1.86914 12.6113 3.875C13.7793 5.90625 13.7793 8.36914 12.6113 10.375C11.4434 12.4062 9.31055 13.625 7 13.625ZM7 3.875C6.64453 3.875 6.39062 4.1543 6.39062 4.48438V7.32812C6.39062 7.68359 6.64453 7.9375 7 7.9375C7.33008 7.9375 7.60938 7.68359 7.60938 7.32812V4.48438C7.60938 4.1543 7.33008 3.875 7 3.875ZM6.1875 9.5625C6.1875 10.0195 6.54297 10.375 7 10.375C7.43164 10.375 7.8125 10.0195 7.8125 9.5625C7.8125 9.13086 7.43164 8.75 7 8.75C6.54297 8.75 6.1875 9.13086 6.1875 9.5625Z"
                                        fill="#8E6807"
                                    />
                                </svg>
                                <span className="text-darwin-accent-dark-yellow text-12 font-700">
                                    Enter age to use growth chart
                                </span>
                            </div>
                        )}
                    </div>

                    <button
                        onClick={
                            ageInNumber &&
                            ageInNumber >= 0 &&
                            ageInNumber <= 18 &&
                            (patientPersonalProfile?.gender?.toLowerCase() === 'male' ||
                                patientPersonalProfile?.gender?.toLowerCase() === 'female')
                                ? this.openGowthChartRightPopUp
                                : undefined
                        }
                        disabled={
                            !(
                                ageInNumber &&
                                ageInNumber >= 0 &&
                                ageInNumber <= 18 &&
                                (patientPersonalProfile?.gender?.toLowerCase() === 'male' ||
                                    patientPersonalProfile?.gender?.toLowerCase() === 'female')
                            )
                        }
                        className="primary-btn px-16 py-6 body-2 space-x-8 font-400"
                    >
                        <span className="text-14 font-700 text-white whitespace-nowrap">
                            View growth chart
                        </span>
                        <svg
                            className="w-16 h-16 fill-white"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 448 512"
                        >
                            <path d="M264.6 70.63l176 168c4.75 4.531 7.438 10.81 7.438 17.38s-2.688 12.84-7.438 17.38l-176 168c-9.594 9.125-24.78 8.781-33.94-.8125c-9.156-9.5-8.812-24.75 .8125-33.94l132.7-126.6H24.01c-13.25 0-24.01-10.76-24.01-24.01s10.76-23.99 24.01-23.99h340.1l-132.7-126.6C221.8 96.23 221.5 80.98 230.6 71.45C239.8 61.85 254.1 61.51 264.6 70.63z"></path>
                        </svg>
                    </button>
                </div>
                <AnimatePresence>
                    {openGrowthChartRightPopUp && (
                        <GrowthChartRightPopUp closeMe={this.closeGowthChartRightPopUp} />
                    )}
                </AnimatePresence>
            </>
        );
    }
}

export default connect((state: GlobalState) => ({
    patientPersonalProfile: getPatientPersonalProfile(state),
}))(GrowthChartPad);
